import React, { type CSSProperties } from "react";
import {
  ArrowRight,
  BookOpen,
  ChevronRight,
  ExternalLink,
  FileText,
  Newspaper,
  Phone,
  ShieldCheck,
  Sparkles,
  Wrench,
} from "lucide-react";
import {
  ArrowRightIcon as HeroArrowRightIcon,
  BookOpenIcon as HeroBookOpenIcon,
  ChevronRightIcon as HeroChevronRightIcon,
  DocumentTextIcon as HeroDocumentTextIcon,
  NewspaperIcon as HeroNewspaperIcon,
  PhoneIcon as HeroPhoneIcon,
  SparklesIcon as HeroSparklesIcon,
  WrenchScrewdriverIcon as HeroWrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import {
  sidebarArticleTokens,
  type SidebarArticleColorToken,
  type SidebarArticleIconToken,
} from "./SidebarArticle.tokens";

type SidebarArticleStyle = CSSProperties &
  Record<`--sidebar-article-${string}`, string | number | undefined>;

type SidebarArticleItemStyle = CSSProperties &
  Record<`--sidebar-article-item-${string}`, string | number | undefined>;

type SidebarArticleIconComponent = React.ElementType;

const t = sidebarArticleTokens;

const darkSidebarArticleColors = {
  background: "#081225",
  surface: "#0d1930",
  text: "#ffffff",
  textSoft: "rgba(255, 255, 255, 0.78)",
  textMuted: "rgba(255, 255, 255, 0.62)",
  border: "rgba(255, 255, 255, 0.16)",
  imageFallback: "rgba(255, 255, 255, 0.08)",
  shadow: "0 18px 48px rgba(0, 0, 0, 0.24)",
  hoverShadow: "0 22px 54px rgba(0, 0, 0, 0.32)",
};

const iconRegistry: Record<SidebarArticleIconToken, SidebarArticleIconComponent> = {
  arrowRight: ArrowRight,
  bookOpen: BookOpen,
  chevronRight: ChevronRight,
  externalLink: ExternalLink,
  fileText: FileText,
  newspaper: Newspaper,
  phone: Phone,
  shieldCheck: ShieldCheck,
  sparkles: Sparkles,
  wrench: Wrench,
  heroArrowRight: HeroArrowRightIcon,
  heroBookOpen: HeroBookOpenIcon,
  heroChevronRight: HeroChevronRightIcon,
  heroDocumentText: HeroDocumentTextIcon,
  heroNewspaper: HeroNewspaperIcon,
  heroPhone: HeroPhoneIcon,
  heroSparkles: HeroSparklesIcon,
  heroWrenchScrewdriver: HeroWrenchScrewdriverIcon,
};

export type SidebarArticleLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  "aria-label"?: string;
  style?: CSSProperties;
  [key: string]: any;
}>;

export type SidebarArticleImage = {
  src?: string;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  originalAlt?: string;
  thumbnailAlt?: string;
  objectPosition?: string;
};

export type SidebarArticleCta = {
  label: React.ReactNode;
  href: string;
  ariaLabel?: string;
  icon?: React.ReactNode;
  iconToken?: SidebarArticleIconToken;
};

export type SidebarArticleFooterCta = SidebarArticleCta & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  accentColor?: string;
  accentColour?: string;
  ctaIcon?: React.ReactNode;
  ctaIconToken?: SidebarArticleIconToken;
};

export type SidebarArticleItem = {
  id: string | number;
  title: React.ReactNode;
  description?: React.ReactNode;
  href?: string;
  ctaLabel?: React.ReactNode;
  ctaAriaLabel?: string;
  image?: SidebarArticleImage;
  imageUrl?: string;
  imageAlt?: string;
  imagePosition?: string;
  eyebrow?: React.ReactNode;
  icon?: React.ReactNode;
  iconToken?: SidebarArticleIconToken;
  arrowIcon?: React.ReactNode;
  arrowIconToken?: SidebarArticleIconToken;
  accentColor?: string;
  accentColour?: string;
};

export type SidebarArticleColors = Partial<Record<SidebarArticleColorToken, string>>;

export type SidebarArticleProps = {
  items: SidebarArticleItem[];
  Link?: SidebarArticleLinkComponent;
  title?: React.ReactNode;
  titleComponent?: "h2" | "h3" | "h4" | "h5" | "h6";
  description?: React.ReactNode;
  headerCta?: SidebarArticleCta;
  footerCta?: SidebarArticleFooterCta;
  itemCtaLabel?: React.ReactNode;
  itemCtaIcon?: React.ReactNode;
  itemCtaIconToken?: SidebarArticleIconToken;
  colors?: SidebarArticleColors;
  colours?: SidebarArticleColors;
  accentColor?: string;
  accentColour?: string;
  backgroundColor?: string;
  backgroundColour?: string;
  surfaceColor?: string;
  surfaceColour?: string;
  textColor?: string;
  textColour?: string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  scrollable?: boolean;
  imageWidth?: number | string;
  imageHeight?: number | string;
  showItemArrows?: boolean;
  dark?: boolean;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  "data-entity"?: string;
};

const DefaultLink: SidebarArticleLinkComponent = ({ href, children, ...props }) => (
  <a href={href} {...props}>
    {children}
  </a>
);

const sidebarArticleCss = `
.sidebar-article {
  --sidebar-article-bg: ${t.colors.background};
  --sidebar-article-surface: ${t.colors.surface};
  --sidebar-article-text: ${t.colors.text};
  --sidebar-article-text-soft: ${t.colors.textSoft};
  --sidebar-article-text-muted: ${t.colors.textMuted};
  --sidebar-article-accent: ${t.colors.accent};
  --sidebar-article-accent-contrast: ${t.colors.accentContrast};
  --sidebar-article-accent-soft: color-mix(in srgb, var(--sidebar-article-accent) 10%, transparent);
  --sidebar-article-border: ${t.colors.border};
  --sidebar-article-image-fallback: ${t.colors.imageFallback};
  --sidebar-article-shadow: ${t.colors.shadow};
  --sidebar-article-hover-shadow: ${t.colors.hoverShadow};
  --sidebar-article-max-width: ${t.sizes.maxWidth}px;
  --sidebar-article-max-height: none;
  --sidebar-article-image-width: ${t.sizes.imageWidth}px;
  --sidebar-article-image-height: ${t.sizes.imageHeight}px;
  width: 100%;
  color: var(--sidebar-article-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
}
.sidebar-article,
.sidebar-article * {
  box-sizing: border-box;
}
.sidebar-article-panel {
  width: 100%;
  max-width: var(--sidebar-article-max-width);
  margin: 0;
  padding: ${t.spacing.panel}px;
  border: 1px solid var(--sidebar-article-border);
  border-radius: ${t.radius.panel}px;
  background: var(--sidebar-article-bg);
  box-shadow: var(--sidebar-article-shadow);
}
.sidebar-article--scrollable .sidebar-article-panel {
  display: flex;
  max-height: var(--sidebar-article-max-height);
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
}
.sidebar-article-header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${t.spacing.headerGap}px;
}
.sidebar-article-heading {
  min-width: 0;
}
.sidebar-article-title {
  margin: 0;
  color: var(--sidebar-article-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
  overflow-wrap: anywhere;
}
.sidebar-article-rule {
  display: block;
  width: 46px;
  height: 3px;
  margin-top: 13px;
  border-radius: 999px;
  background: var(--sidebar-article-accent);
}
.sidebar-article-description {
  margin: 12px 0 0;
  color: var(--sidebar-article-text-soft);
  font-size: ${t.typography.description.fontSize}px;
  line-height: ${t.typography.description.lineHeight};
  font-weight: ${t.typography.description.fontWeight};
  letter-spacing: ${t.typography.description.letterSpacing};
  overflow-wrap: anywhere;
}
.sidebar-article-header-link,
.sidebar-article-footer-link,
.sidebar-article-link {
  color: inherit;
  text-decoration: none;
}
.sidebar-article-header-link {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 7px;
  min-height: 30px;
  padding-top: 4px;
  color: var(--sidebar-article-accent);
  font-size: ${t.typography.headerCta.fontSize}px;
  line-height: ${t.typography.headerCta.lineHeight};
  font-weight: ${t.typography.headerCta.fontWeight};
  letter-spacing: ${t.typography.headerCta.letterSpacing};
  white-space: nowrap;
}
.sidebar-article-header-link .sidebar-article-icon {
  transition: ${t.motion.icon};
}
.sidebar-article-header-link:hover .sidebar-article-icon,
.sidebar-article-header-link:focus-visible .sidebar-article-icon {
  transform: translateX(3px);
}
.sidebar-article-header-link:focus-visible,
.sidebar-article-link:focus-visible,
.sidebar-article-footer-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--sidebar-article-accent) 26%, transparent);
  outline-offset: 4px;
}
.sidebar-article-list {
  display: grid;
  gap: ${t.spacing.listGap}px;
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
}
.sidebar-article--scrollable .sidebar-article-list {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 8px;
  scrollbar-gutter: stable;
  -webkit-overflow-scrolling: touch;
}
.sidebar-article--scrollable .sidebar-article-list::-webkit-scrollbar {
  width: 8px;
}
.sidebar-article--scrollable .sidebar-article-list::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar-article--scrollable .sidebar-article-list::-webkit-scrollbar-thumb {
  border: 2px solid var(--sidebar-article-bg);
  border-radius: 999px;
  background: color-mix(in srgb, var(--sidebar-article-accent) 46%, var(--sidebar-article-border));
}
.sidebar-article-link {
  display: block;
  border-radius: ${t.radius.card}px;
}
.sidebar-article-card {
  --sidebar-article-card-accent: var(--sidebar-article-item-accent, var(--sidebar-article-accent));
  display: grid;
  grid-template-columns: var(--sidebar-article-image-width) minmax(0, 1fr) auto;
  align-items: center;
  min-height: var(--sidebar-article-image-height);
  overflow: hidden;
  border: 1px solid var(--sidebar-article-border);
  border-radius: ${t.radius.card}px;
  background: var(--sidebar-article-surface);
  transition: ${t.motion.card};
}
.sidebar-article-link:hover .sidebar-article-card,
.sidebar-article-link:focus-visible .sidebar-article-card {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--sidebar-article-card-accent) 32%, var(--sidebar-article-border));
  background: color-mix(in srgb, var(--sidebar-article-card-accent) 4%, var(--sidebar-article-surface));
  box-shadow: var(--sidebar-article-hover-shadow);
}
.sidebar-article-media {
  position: relative;
  align-self: stretch;
  min-height: var(--sidebar-article-image-height);
  overflow: hidden;
  background: var(--sidebar-article-image-fallback);
}
.sidebar-article-image,
.sidebar-article-image-fallback {
  display: block;
  width: 100%;
  height: 100%;
  min-height: var(--sidebar-article-image-height);
}
.sidebar-article-image {
  object-fit: cover;
  transform: scale(1);
  transition: ${t.motion.image};
}
.sidebar-article-link:hover .sidebar-article-image,
.sidebar-article-link:focus-visible .sidebar-article-image {
  transform: scale(1.075);
}
.sidebar-article-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: var(--sidebar-article-card-accent);
  background: color-mix(in srgb, var(--sidebar-article-card-accent) 11%, var(--sidebar-article-image-fallback));
}
.sidebar-article-image-fallback .sidebar-article-icon {
  opacity: 0.86;
}
.sidebar-article-content {
  min-width: 0;
  padding: 12px ${t.spacing.cardGap}px;
}
.sidebar-article-eyebrow {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: 6px;
  margin: 0 0 7px;
  color: var(--sidebar-article-card-accent);
  font-size: ${t.typography.eyebrow.fontSize}px;
  line-height: ${t.typography.eyebrow.lineHeight};
  font-weight: ${t.typography.eyebrow.fontWeight};
  letter-spacing: ${t.typography.eyebrow.letterSpacing};
  text-transform: ${t.typography.eyebrow.textTransform};
}
.sidebar-article-eyebrow-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.sidebar-article-item-title {
  margin: 0;
  color: var(--sidebar-article-text);
  font-size: ${t.typography.itemTitle.fontSize}px;
  line-height: ${t.typography.itemTitle.lineHeight};
  font-weight: ${t.typography.itemTitle.fontWeight};
  letter-spacing: ${t.typography.itemTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.sidebar-article-item-text {
  display: -webkit-box;
  margin: 8px 0 0;
  overflow: hidden;
  color: var(--sidebar-article-text-soft);
  font-size: ${t.typography.itemText.fontSize}px;
  line-height: ${t.typography.itemText.lineHeight};
  font-weight: ${t.typography.itemText.fontWeight};
  letter-spacing: ${t.typography.itemText.letterSpacing};
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.sidebar-article-arrow {
  width: ${t.sizes.arrow}px;
  height: ${t.sizes.arrow}px;
  display: inline-flex;
  flex: 0 0 ${t.sizes.arrow}px;
  align-items: center;
  justify-content: center;
  margin-right: ${t.spacing.cardPadding}px;
  border: 1px solid color-mix(in srgb, var(--sidebar-article-card-accent) 38%, var(--sidebar-article-border));
  border-radius: ${t.radius.arrow}px;
  background: transparent;
  color: var(--sidebar-article-card-accent);
  transition: ${t.motion.arrow};
}
.sidebar-article-link:hover .sidebar-article-arrow,
.sidebar-article-link:focus-visible .sidebar-article-arrow {
  transform: translateX(2px);
  border-color: var(--sidebar-article-card-accent);
  background: var(--sidebar-article-card-accent);
  color: var(--sidebar-article-accent-contrast);
}
.sidebar-article-icon {
  width: var(--sidebar-article-icon-size, 16px);
  height: var(--sidebar-article-icon-size, 16px);
  display: inline-flex;
  flex: 0 0 var(--sidebar-article-icon-size, 16px);
  align-items: center;
  justify-content: center;
}
.sidebar-article-icon svg,
svg.sidebar-article-icon {
  width: var(--sidebar-article-icon-size, 16px);
  height: var(--sidebar-article-icon-size, 16px);
  display: block;
}
.sidebar-article-footer {
  flex: 0 0 auto;
  margin-top: 14px;
}
.sidebar-article-footer-link {
  --sidebar-article-footer-accent: var(--sidebar-article-footer-cta-accent, var(--sidebar-article-accent));
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: ${t.spacing.footerPaddingY}px ${t.spacing.footerPaddingX}px;
  border: 1px solid color-mix(in srgb, var(--sidebar-article-footer-accent) 10%, transparent);
  border-radius: ${t.radius.card}px;
  background: color-mix(in srgb, var(--sidebar-article-footer-accent) 9%, var(--sidebar-article-surface));
  color: var(--sidebar-article-text);
  transition: ${t.motion.card};
}
.sidebar-article-footer-link:hover,
.sidebar-article-footer-link:focus-visible {
  border-color: color-mix(in srgb, var(--sidebar-article-footer-accent) 28%, var(--sidebar-article-border));
  background: color-mix(in srgb, var(--sidebar-article-footer-accent) 13%, var(--sidebar-article-surface));
}
.sidebar-article-footer-icon {
  width: ${t.sizes.footerIcon}px;
  height: ${t.sizes.footerIcon}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${t.radius.arrow}px;
  color: var(--sidebar-article-footer-accent);
  background: color-mix(in srgb, var(--sidebar-article-footer-accent) 13%, transparent);
}
.sidebar-article-footer-copy {
  min-width: 0;
}
.sidebar-article-footer-title {
  display: block;
  margin: 0;
  color: var(--sidebar-article-text);
  font-size: ${t.typography.footerTitle.fontSize}px;
  line-height: ${t.typography.footerTitle.lineHeight};
  font-weight: ${t.typography.footerTitle.fontWeight};
  letter-spacing: ${t.typography.footerTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.sidebar-article-footer-text {
  display: block;
  margin: 3px 0 0;
  color: var(--sidebar-article-text-soft);
  font-size: ${t.typography.footerText.fontSize}px;
  line-height: ${t.typography.footerText.lineHeight};
  font-weight: ${t.typography.footerText.fontWeight};
  letter-spacing: ${t.typography.footerText.letterSpacing};
  overflow-wrap: anywhere;
}
.sidebar-article-footer-cta {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  color: var(--sidebar-article-footer-accent);
  font-size: ${t.typography.footerCta.fontSize}px;
  line-height: ${t.typography.footerCta.lineHeight};
  font-weight: ${t.typography.footerCta.fontWeight};
  letter-spacing: ${t.typography.footerCta.letterSpacing};
  white-space: nowrap;
}
.sidebar-article-footer-cta .sidebar-article-icon {
  transition: ${t.motion.icon};
}
.sidebar-article-footer-link:hover .sidebar-article-footer-cta .sidebar-article-icon,
.sidebar-article-footer-link:focus-visible .sidebar-article-footer-cta .sidebar-article-icon {
  transform: translateX(3px);
}
@media (max-width: 420px) {
  .sidebar-article-panel {
    padding: 16px;
  }
  .sidebar-article-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  }
  .sidebar-article-header-link {
    justify-self: start;
    padding-top: 0;
  }
  .sidebar-article-card {
    grid-template-columns: minmax(92px, 32%) minmax(0, 1fr) auto;
  }
  .sidebar-article-content {
    padding: 11px 10px;
  }
  .sidebar-article-arrow {
    width: 30px;
    height: 30px;
    flex-basis: 30px;
    margin-right: 10px;
  }
  .sidebar-article-footer-link {
    grid-template-columns: auto minmax(0, 1fr);
  }
  .sidebar-article-footer-cta {
    grid-column: 2;
    justify-content: flex-start;
  }
}
@media (max-width: 340px) {
  .sidebar-article-card {
    grid-template-columns: minmax(0, 1fr) auto;
  }
  .sidebar-article-media {
    display: none;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function resolveIconToken(token?: SidebarArticleIconToken): SidebarArticleIconToken | undefined {
  if (!token || !(token in iconRegistry)) return undefined;
  return token;
}

function SidebarArticleIcon({
  icon,
  token,
  fallbackToken,
  size = 16,
  className = "sidebar-article-icon",
}: {
  icon?: React.ReactNode;
  token?: SidebarArticleIconToken;
  fallbackToken?: SidebarArticleIconToken;
  size?: number;
  className?: string;
}) {
  if (icon) {
    return (
      <span
        className={className}
        style={{ "--sidebar-article-icon-size": `${size}px` } as SidebarArticleStyle}
        aria-hidden="true"
      >
        {icon}
      </span>
    );
  }

  const resolvedToken = resolveIconToken(token) || resolveIconToken(fallbackToken);
  const Icon = resolvedToken ? iconRegistry[resolvedToken] : undefined;

  if (!Icon) return null;

  return (
    <Icon
      className={className}
      style={{ "--sidebar-article-icon-size": `${size}px` } as SidebarArticleStyle}
      aria-hidden="true"
      strokeWidth={2.35}
    />
  );
}

function getImage(item: SidebarArticleItem): SidebarArticleImage {
  if (item.image) return item.image;

  if (item.imageUrl) {
    return {
      src: item.imageUrl,
      alt: item.imageAlt,
      objectPosition: item.imagePosition,
    };
  }

  return {};
}

function getImageSrc(image: SidebarArticleImage): string {
  return image.thumbnail || image.src || image.url || image.original || "";
}

function getImageAlt(image: SidebarArticleImage, title: React.ReactNode): string {
  if (image.alt || image.thumbnailAlt || image.originalAlt || image.title) {
    return image.alt || image.thumbnailAlt || image.originalAlt || image.title || "";
  }

  return typeof title === "string" ? title : "Article image";
}

function getItemAriaLabel(item: SidebarArticleItem, fallbackLabel?: React.ReactNode): string | undefined {
  if (item.ctaAriaLabel) return item.ctaAriaLabel;
  if (typeof item.title !== "string") return undefined;

  const ctaLabel = item.ctaLabel || fallbackLabel;

  if (typeof ctaLabel === "string") {
    return `${ctaLabel}: ${item.title}`;
  }

  return item.title;
}

function getColors({
  colors,
  colours,
  accentColor,
  accentColour,
  backgroundColor,
  backgroundColour,
  surfaceColor,
  surfaceColour,
  textColor,
  textColour,
}: Pick<
  SidebarArticleProps,
  | "colors"
  | "colours"
  | "accentColor"
  | "accentColour"
  | "backgroundColor"
  | "backgroundColour"
  | "surfaceColor"
  | "surfaceColour"
  | "textColor"
  | "textColour"
>,
  dark = false,
) {
  const resolved = {
    ...t.colors,
    ...(dark ? darkSidebarArticleColors : undefined),
    ...colours,
    ...colors,
  };

  return {
    ...resolved,
    accent: accentColor || accentColour || resolved.accent,
    background: backgroundColor || backgroundColour || resolved.background,
    surface: surfaceColor || surfaceColour || resolved.surface,
    text: textColor || textColour || resolved.text,
  };
}

export default function SidebarArticle({
  items,
  Link = DefaultLink,
  title = "Related Articles",
  titleComponent = "h2",
  description,
  headerCta,
  footerCta,
  itemCtaLabel,
  itemCtaIcon,
  itemCtaIconToken = t.icons.itemCta as SidebarArticleIconToken,
  colors,
  colours,
  accentColor,
  accentColour,
  backgroundColor,
  backgroundColour,
  surfaceColor,
  surfaceColour,
  textColor,
  textColour,
  maxWidth = t.sizes.maxWidth,
  maxHeight = "none",
  scrollable = false,
  imageWidth = t.sizes.imageWidth,
  imageHeight = t.sizes.imageHeight,
  showItemArrows = true,
  dark = false,
  className = "",
  style,
  ariaLabel,
  "data-entity": dataEntity,
}: SidebarArticleProps) {
  if (!items?.length) return null;

  const TitleTag = titleComponent;
  const resolvedColors = getColors({
    colors,
    colours,
    accentColor,
    accentColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
  }, dark);
  const rootStyle = {
    "--sidebar-article-bg": resolvedColors.background,
    "--sidebar-article-surface": resolvedColors.surface,
    "--sidebar-article-text": resolvedColors.text,
    "--sidebar-article-text-soft": resolvedColors.textSoft,
    "--sidebar-article-text-muted": resolvedColors.textMuted,
    "--sidebar-article-accent": resolvedColors.accent,
    "--sidebar-article-accent-contrast": resolvedColors.accentContrast,
    "--sidebar-article-border": resolvedColors.border,
    "--sidebar-article-image-fallback": resolvedColors.imageFallback,
    "--sidebar-article-shadow": resolvedColors.shadow,
    "--sidebar-article-hover-shadow": resolvedColors.hoverShadow,
    "--sidebar-article-max-width": getCssSize(maxWidth),
    "--sidebar-article-max-height": getCssSize(maxHeight),
    "--sidebar-article-image-width": getCssSize(imageWidth),
    "--sidebar-article-image-height": getCssSize(imageHeight),
    ...style,
  } as SidebarArticleStyle;

  return (
    <aside
      className={[
        "sidebar-article",
        scrollable ? "sidebar-article--scrollable" : "",
        className,
      ].filter(Boolean).join(" ")}
      data-component="sidebar-article"
      data-entity={dataEntity}
      data-theme={dark ? "dark" : "light"}
      aria-label={ariaLabel || (typeof title === "string" ? title : "Related articles")}
      style={rootStyle}
    >
      <style>{sidebarArticleCss}</style>

      <div className="sidebar-article-panel">
        <div className="sidebar-article-header">
          <div className="sidebar-article-heading">
            {title ? <TitleTag className="sidebar-article-title">{title}</TitleTag> : null}
            <span className="sidebar-article-rule" aria-hidden="true" />
            {description ? <p className="sidebar-article-description">{description}</p> : null}
          </div>

          {headerCta ? (
            <Link
              href={headerCta.href}
              className="sidebar-article-header-link"
              aria-label={headerCta.ariaLabel}
            >
              <span>{headerCta.label}</span>
              <SidebarArticleIcon
                icon={headerCta.icon}
                token={headerCta.iconToken}
                fallbackToken={t.icons.headerCta as SidebarArticleIconToken}
                size={14}
              />
            </Link>
          ) : null}
        </div>

        <ul className="sidebar-article-list">
          {items.map((item) => {
            const image = getImage(item);
            const imageSrc = getImageSrc(image);
            const itemAccent = item.accentColor || item.accentColour || resolvedColors.accent;
            const itemStyle = {
              "--sidebar-article-item-accent": itemAccent,
            } as SidebarArticleItemStyle;
            const href = item.href || "#";

            return (
              <li key={item.id} style={itemStyle}>
                <Link
                  href={href}
                  title={typeof item.title === "string" ? item.title : undefined}
                  aria-label={getItemAriaLabel(item, itemCtaLabel)}
                  className="sidebar-article-link"
                >
                  <article className="sidebar-article-card">
                    <div className="sidebar-article-media">
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt={getImageAlt(image, item.title)}
                          className="sidebar-article-image"
                          loading="lazy"
                          style={{ objectPosition: image.objectPosition }}
                        />
                      ) : (
                        <div className="sidebar-article-image-fallback">
                          <SidebarArticleIcon
                            icon={item.icon}
                            token={item.iconToken}
                            fallbackToken={t.icons.fallback as SidebarArticleIconToken}
                            size={30}
                          />
                        </div>
                      )}
                    </div>

                    <div className="sidebar-article-content">
                      {item.eyebrow ? (
                        <p className="sidebar-article-eyebrow">
                          <SidebarArticleIcon
                            icon={item.icon}
                            token={item.iconToken}
                            fallbackToken={t.icons.fallback as SidebarArticleIconToken}
                            size={12}
                          />
                          <span className="sidebar-article-eyebrow-text">{item.eyebrow}</span>
                        </p>
                      ) : null}
                      <h3 className="sidebar-article-item-title">{item.title}</h3>
                      {item.description ? (
                        <p className="sidebar-article-item-text">{item.description}</p>
                      ) : null}
                    </div>

                    {showItemArrows ? (
                      <span className="sidebar-article-arrow" aria-hidden="true">
                        <SidebarArticleIcon
                          icon={item.arrowIcon || itemCtaIcon}
                          token={item.arrowIconToken || itemCtaIconToken}
                          fallbackToken={t.icons.itemCta as SidebarArticleIconToken}
                          size={17}
                        />
                      </span>
                    ) : null}
                  </article>
                </Link>
              </li>
            );
          })}
        </ul>

        {footerCta ? (
          <div
            className="sidebar-article-footer"
            style={
              {
                "--sidebar-article-footer-cta-accent":
                  footerCta.accentColor || footerCta.accentColour || resolvedColors.accent,
              } as SidebarArticleStyle
            }
          >
            <Link
              href={footerCta.href}
              className="sidebar-article-footer-link"
              aria-label={footerCta.ariaLabel}
            >
              <span className="sidebar-article-footer-icon" aria-hidden="true">
                <SidebarArticleIcon
                  icon={footerCta.icon}
                  token={footerCta.iconToken}
                  fallbackToken={t.icons.footer as SidebarArticleIconToken}
                  size={20}
                />
              </span>

              <span className="sidebar-article-footer-copy">
                {footerCta.title ? (
                  <span className="sidebar-article-footer-title">{footerCta.title}</span>
                ) : null}
                {footerCta.description ? (
                  <span className="sidebar-article-footer-text">{footerCta.description}</span>
                ) : null}
              </span>

              <span className="sidebar-article-footer-cta">
                <span>{footerCta.label}</span>
                <SidebarArticleIcon
                  icon={footerCta.ctaIcon}
                  token={footerCta.ctaIconToken}
                  fallbackToken={t.icons.footerCta as SidebarArticleIconToken}
                  size={14}
                />
              </span>
            </Link>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

export {
  sidebarArticleIconTokens,
  sidebarArticleTokens,
  type SidebarArticleColorToken,
  type SidebarArticleIconToken,
} from "./SidebarArticle.tokens";
